import "./qa-premium.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { useQaPerms, formatDate } from "./qa-shared";

export default function QaVisitList() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const perms = useQaPerms();
  const { data: visits = [], isLoading, refetch } = trpc.qa.visits.list.useQuery({ limit: 200 });

  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const COLUMNS = [
    { status: "scheduled", label: t("qa.status.scheduled"), color: "var(--p)" },
    { status: "questionnaire_submitted", label: t("qa.status.submitted"), color: "var(--blue)" },
    { status: "under_review", label: t("qa.status.underReview"), color: "var(--amber)" },
    { status: "assessment_submitted", label: t("qa.status.assessment"), color: "var(--p-mid)" },
    { status: "report_approved", label: t("qa.status.approved"), color: "var(--green)" },
  ];
  const LABELS: Record<string, string> = {
    scheduled: t("qa.status.scheduled"), questionnaire_submitted: t("qa.status.submitted"), under_review: t("qa.status.underReview"),
    assessment_submitted: t("qa.status.assessment"), report_approved: t("qa.status.approved"),
  };
  const setStatus = trpc.qa.visits.setStatus.useMutation({
    onMutate: ({ visitId, status }) => {
      // optimistic: move the card immediately, then reconcile on settle
      utils.qa.visits.list.setData({ limit: 200 } as any, (old: any) =>
        Array.isArray(old) ? old.map((x: any) => (x.id === visitId ? { ...x, status } : x)) : old);
    },
    onSuccess: (_d, vars) => { toast.success(`${t("qa.visits.movedTo")} ${LABELS[vars.status]}`); },
    onError: (e) => toast.error(e.message),
    onSettled: () => refetch(),
  });

  const canDrag = perms.visitManage || perms.reviewManage;

  function handleDrop(toStatus: string) {
    const v = visits.find((x: any) => x.id === draggingId);
    setOverCol(null);
    setDraggingId(null);
    if (!v || v.status === toStatus) return;
    setStatus.mutate({ visitId: v.id, status: toStatus as any });
  }

  return (
    <div className="qa-premium full">
      <div className="hero tight">
        <div className="top">
          <div>
            <h1>{t("qa.visits.title")}</h1>
            <p>{t("qa.visits.subtitle")}</p>
          </div>
          <div className="acts">
            <button className="btn btn-glass" onClick={() => setLocation("/qa")}><i className="ti ti-layout-dashboard" /> {t("qa.dashboard.title")}</button>
            {perms.visitManage && <button className="btn btn-light" onClick={() => setLocation("/qa/visits/new")}><i className="ti ti-plus" /> {t("qa.dashboard.scheduleVisit")}</button>}
          </div>
        </div>
      </div>

      <div className="wrap" style={{ paddingTop: 0 }}>
        {canDrag && (
          <div className="gk-hint"><i className="ti ti-hand-finger" /> {t("qa.visits.dragHint")}</div>
        )}

        {isLoading ? (
          <div className="loading" style={{ marginTop: 22 }}><span className="spin" /></div>
        ) : (
          <div className="gk-board">
            {COLUMNS.map((col) => {
              const items = visits.filter((v: any) => v.status === col.status);
              return (
                <div
                  key={col.status}
                  className={`gk-col ${overCol === col.status ? "over" : ""}`}
                  onDragOver={(e) => { if (canDrag && draggingId != null) { e.preventDefault(); setOverCol(col.status); } }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setOverCol((c) => (c === col.status ? null : c)); }}
                  onDrop={(e) => { e.preventDefault(); handleDrop(col.status); }}
                >
                  <div className="gk-colh" style={{ background: col.color }}>
                    {col.label} <span className="n">{items.length}</span>
                  </div>
                  <div className={`gk-body ${items.length === 0 ? "is-empty" : ""}`}>
                    {items.map((v: any) => (
                        <div
                          key={v.id}
                          className={`gk-card ${canDrag ? "draggable" : ""} ${draggingId === v.id ? "dragging" : ""}`}
                          draggable={canDrag}
                          onDragStart={() => setDraggingId(v.id)}
                          onDragEnd={() => { setDraggingId(null); setOverCol(null); }}
                          onClick={() => setLocation(`/qa/visits/${v.id}`)}
                        >
                          <div className="num">{v.visitNumber}</div>
                          <div className="site">{v.siteName ?? `${t("qa.visits.site")} #${v.siteId}`}</div>
                          <div className="foot">
                            <span className="dt"><i className="ti ti-calendar" /> {formatDate(v.visitDate)}</span>
                            {v.status === "report_approved" && v.overallScore != null && (
                              <span className="scorebadge">{Math.round(Number(v.overallScore))}%</span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ height: 30 }} />
      </div>
    </div>
  );
}
